/**
 * GitHubCredModal — standalone connect modal for GitHub, mirroring
 * IntegrationsPage.jsx's TwitterCredModal chrome/contract ({onClose, onSaved}).
 * Extracted so it can be opened inline from the New Task chat (RunDrawer.jsx)
 * without navigating away to the Integrations page. Same save call
 * (gitAPI.saveGithubToken) GitHubCard on the Integrations page already uses
 * and already live-verifies the token server-side before saving.
 */
import React, { useState } from "react";
import { Github, X, Eye, EyeOff, Check, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { gitAPI } from "../../services/api.js";

export default function GitHubCredModal({ onClose, onSaved }) {
  const [token, setToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!token.trim()) { toast.error("Paste your GitHub PAT first."); return; }
    setSaving(true);
    try {
      const d = await gitAPI.saveGithubToken(token.trim());
      toast.success(`GitHub connected as @${d.username}`);
      onSaved({ username: d.username });
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--lyra-color-surface)", borderRadius: 20, width: "100%", maxWidth: 440, boxShadow: "0 24px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg,var(--lyra-color-text),var(--lyra-color-text))", padding: "20px 24px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Github size={22} color="var(--app-on-accent)" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--app-on-accent)" }}>Connect GitHub</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>Token verified live before saving</p>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer", borderRadius: 8, padding: 6, display: "flex", flexShrink: 0 }}><X size={16} color="var(--app-on-accent)" /></button>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: 13, color: "var(--app-text-2)", marginBottom: 5 }}>Personal Access Token</label>
            <div style={{ position: "relative" }}>
              <input
                type={showToken ? "text" : "password"}
                value={token}
                onChange={e => setToken(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSave()}
                placeholder="ghp_…"
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 36px 9px 12px", border: "1.5px solid var(--lyra-color-border)", borderRadius: 9, fontSize: 13, outline: "none", fontFamily: "inherit" }}
              />
              <button type="button" onClick={() => setShowToken(v => !v)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--app-text-3)", padding: 0 }}>
                {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--app-text-3)", marginTop: 6, lineHeight: 1.4 }}>
              Generate a PAT at <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" style={{ color: "var(--lyra-color-text)", fontWeight: 600 }}>github.com/settings/tokens</a> with <code style={{ fontSize: 10.5, background: "var(--app-surface-3)", padding: "1px 5px", borderRadius: 4 }}>repo</code> scope
            </div>
          </div>
          <button onClick={handleSave} disabled={saving || !token.trim()} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", background: token.trim() ? "var(--lyra-color-text)" : "var(--lyra-color-border)", color: token.trim() ? "var(--lyra-color-surface)" : "var(--app-text-3)", borderRadius: 10, padding: "11px 0", fontWeight: 700, fontSize: 14, cursor: token.trim() ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
            {saving ? <><Loader2 size={15} style={{ animation: "spin 0.7s linear infinite" }} />Verifying…</> : <><Check size={15} />Connect GitHub</>}
          </button>
        </div>
      </div>
    </div>
  );
}
